import { areaUomAbbrev, formatNumber } from '@miq/utils';
import { isNotNullOrEmptyString } from '../shared/utils/functions';
import { FloorOption } from '../stacking-plan-container/dialogs/more-info-dialog/elevators/elevators.model';
import { EDPFloorSpaceInfo } from '../stacking-plan-container/floor-container/floor/floor.model';
import { EDPSpaceInfo } from '../stacking-plan-container/floor-container/floor/item/item.model';
import { LocalizationOptions } from './edp/colloquials';
import { AbstractSpaceClass } from './space/abstract-space.class';
import { SpaceClass, SpacesAndInactiveFlag } from './space/space.class';
import { L10nTranslationService } from 'angular-l10n';
import { LocaleId } from './locale.models';

export interface FloorCatalogOptions {
  disableIfFloorNumberValue: boolean;
  useIndexAsValue: boolean;
  showIndex: boolean;
  showHiddenFloors: boolean;
  showInvalidFloors: boolean;
}

interface FloorOptions {
  localization: LocalizationOptions;
  translationService: L10nTranslationService;
}

export class FloorClass {
  static InvalidIndex = -99;
  static maxNumberOfFloorsAboveGround = 200;
  static maxNumberOfFloorsUnderGround = 50;

  static getMaxNumberOfFloorsAboveGround(): number {
    return this.maxNumberOfFloorsAboveGround;
  }

  static getMaxNumberOfFloorsUnderGround(): number {
    return this.maxNumberOfFloorsUnderGround;
  }

  static isFloorIndexValid(floorIndex: number): boolean {
    return floorIndex >= this.maxNumberOfFloorsUnderGround * -1 && floorIndex <= this.maxNumberOfFloorsAboveGround;
  }

  static clone(floors: FloorClass[]): FloorClass[] {
    const clone = floor => floor.clone();
    return floors.map(clone);
  }

  static getMaxIndex(floors: FloorClass[]): number {
    return floors.reduce((acc, floor) => (floor.floorIndex > acc ? floor.floorIndex : acc), 0);
  }

  static getMaxRealIndex(floors: FloorClass[], considerHiddenFloors = true): number {
    return floors.reduce((acc, floor) => {
      const { floorIndex, floorId, hideFloor } = floor;
      return floor.isIndexValid() && floorId != null && (considerHiddenFloors || !hideFloor) && floorIndex > acc
        ? floorIndex
        : acc;
    }, 0);
  }

  static getMinRealIndex(floors: FloorClass[], considerHiddenFloors = true): number {
    return floors.reduce(
      (acc, { floorIndex, floorId, hideFloor }) =>
        floorId != null && (considerHiddenFloors || !hideFloor) && floorIndex < acc ? floorIndex : acc,
      0
    );
  }

  static getAboveGround(floors: FloorClass[]): FloorClass[] {
    return floors.filter(floor => floor.isAboveGround());
  }

  static getUnderground(floors: FloorClass[]): FloorClass[] {
    return floors.filter(floor => floor.isUnderGround());
  }

  static getInvalidIndexed(floors: FloorClass[]): FloorClass[] {
    return floors.filter(floor => !floor.isIndexValid());
  }

  static getWithId(floors: FloorClass[]): FloorClass[] {
    return floors.filter(floor => isNotNullOrEmptyString(floor.floorId));
  }

  static emptyFloorFactory(floorIndex: number): FloorClass {
    return new FloorClass(null, floorIndex, null, null, null, null, null, null, null, null, null, false, null, null);
  }

  /**
   * get starting floor index based on countries. As per today, US and China are
   * the only countries that start from 1 (MIQ-15399).
   */
  static getStartingFloorIndex(localeID: number): 0 | 1 {
    switch (localeID) {
      case LocaleId.US:
      case LocaleId.ChinaChinese:
      case LocaleId.ChinaEnglish:
        return 1;
      default:
        return 0;
    }
  }

  static generateAboveGroundFloors(
    totalNumberOfFloors: number,
    incomingFloors: FloorClass[],
    startingIndex: 0 | 1 = 0
  ): FloorClass[] {
    if (startingIndex === 0) {
      return this.generateAboveGroundFloorsStartingFrom0(totalNumberOfFloors, incomingFloors);
    }
    return this.generateAboveGroundFloorsStartingFrom1(totalNumberOfFloors, incomingFloors);
  }

  static getRealTotalNumberOfFloors(totalNumberOfFloors: number, maxFloorIndex: number): number {
    return totalNumberOfFloors != null &&
      totalNumberOfFloors > maxFloorIndex &&
      totalNumberOfFloors <= FloorClass.maxNumberOfFloorsAboveGround
      ? totalNumberOfFloors
      : maxFloorIndex;
  }

  static generateAboveGroundFloorsStartingFrom0(
    totalNumberOfFloors: number,
    incomingFloors: FloorClass[]
  ): FloorClass[] {
    const indexedData = FloorClass.getAboveGround(incomingFloors);
    const maxFloorIndex = FloorClass.getMaxRealIndex(indexedData);
    const realTotalNumberOfFloors = FloorClass.getRealTotalNumberOfFloors(totalNumberOfFloors, maxFloorIndex);
    let indexedFloorData = Array(realTotalNumberOfFloors || 0)
      .fill(undefined)
      .map((_, index) => FloorClass.emptyFloorFactory(index));
    if (indexedFloorData.length > 0) {
      FloorClass.getWithId(indexedData).forEach(floor => {
        indexedFloorData[floor.floorIndex] = floor;
      });
    } else {
      indexedFloorData = indexedData.sort((a, b) => a.floorIndex - b.floorIndex);
    }
    return indexedFloorData;
  }

  static generateAboveGroundFloorsStartingFrom1(
    totalNumberOfFloors: number,
    incomingFloors: FloorClass[]
  ): FloorClass[] {
    let positionedFloors = this.generateAboveGroundFloorsStartingFrom0(totalNumberOfFloors, incomingFloors);

    const indexedData = FloorClass.getAboveGround(incomingFloors);
    const maxFloorIndex = FloorClass.getMaxRealIndex(indexedData);
    const realTotalNumberOfFloors = FloorClass.getRealTotalNumberOfFloors(totalNumberOfFloors, maxFloorIndex);

    /*
     * add an extra floor to the array so the last floor matches the desired
     * length of floors, based on the totalNumberOfFloors.
     * This should only happen when the real total number of floors is the
     * input number of floors.
     *
     * If the total number of floors is the same to the max floor index, it should
     * not add an extra floor. This helps when rendering floors where the last one
     * has data.
     */
    if (totalNumberOfFloors === realTotalNumberOfFloors && maxFloorIndex !== realTotalNumberOfFloors) {
      positionedFloors.push(FloorClass.emptyFloorFactory(positionedFloors.length));
    }

    // remove the first floor if it is just a placeholder
    if (positionedFloors[0]?.floorId == null) {
      positionedFloors = positionedFloors.slice(1);
    }

    return positionedFloors;
  }

  static generateUndergroundFloors(undergroundFloors: number, incomingFloors: FloorClass[]) {
    const undergroundData = FloorClass.getUnderground(incomingFloors);
    const absMinFloorIndex = Math.abs(FloorClass.getMinRealIndex(undergroundData));
    const realTotalNumberOfFloors =
      undergroundFloors != null &&
      undergroundFloors > absMinFloorIndex &&
      undergroundFloors < FloorClass.maxNumberOfFloorsUnderGround
        ? undergroundFloors
        : absMinFloorIndex;
    const undergroundFloorData = Array(realTotalNumberOfFloors || 0)
      .fill(undefined)
      .map((_, index) => FloorClass.emptyFloorFactory((index + 1) * -1));
    if (undergroundFloorData.length > 0) {
      // TODO: review index implementation
      FloorClass.getWithId(undergroundData).forEach(floor => {
        undergroundFloorData[floor.floorIndex * -1 - 1] = floor;
      });
      undergroundFloorData.reverse();
    }
    return undergroundFloorData;
  }

  static generateFloorCatalogOptionsFrom(floors: FloorClass[], options: Partial<FloorCatalogOptions>): FloorOption[] {
    return floors
      .filter(
        floor =>
          (options.showHiddenFloors || !floor.hideFloor) &&
          (options.showInvalidFloors || FloorClass.isFloorIndexValid(floor.floorIndex))
      )
      .map<FloorOption>(floor => floor.generateFloorCatalogOption(options));
  }

  static sanitizeFloorIndex(floorIndex: number): number {
    const isAboveGround = floorIndex >= 0;
    let sanitizedFloorNumber: number = floorIndex;
    if (
      (isAboveGround && floorIndex > FloorClass.maxNumberOfFloorsAboveGround) ||
      floorIndex < FloorClass.maxNumberOfFloorsUnderGround * -1
    ) {
      sanitizedFloorNumber = FloorClass.InvalidIndex;
    }
    return sanitizedFloorNumber;
  }

  static fromEDPFloorSpaceInfo(
    {
      floor_id,
      floor_index,
      floor_number,
      floor_desc,
      floor_plan_url,
      floor_size,
      floor_size_uom_desc,
      floor_slab_height,
      floor_slab_height_uom,
      total_gross_area,
      total_gross_area_uom_desc,
      hide_floor_f,
      floor_plan_id,
      is_space_highlighted,
      spaces,
    }: EDPFloorSpaceInfo,
    floorOptions?: Partial<FloorOptions>
  ): FloorClass {
    return new FloorClass(
      floor_id,
      floor_index,
      floor_number,
      floor_desc,
      floor_size,
      floor_size_uom_desc,
      floor_slab_height,
      floor_slab_height_uom,
      total_gross_area,
      total_gross_area_uom_desc,
      floor_plan_url,
      hide_floor_f,
      floor_plan_id,
      spaces,
      undefined,
      undefined,
      is_space_highlighted,
      floorOptions
    );
  }

  static filterOutInvalidFloorsWithNoSpaces(floors: EDPFloorSpaceInfo[]) {
    return floors.filter(
      floor =>
        floor.floor_index !== FloorClass.InvalidIndex ||
        (floor.floor_index === FloorClass.InvalidIndex && floor.spaces.length > 0)
    );
  }

  /**
   * Receives an array of floors and sorts each floor suits array by suite_id in
   * ascending order. This sorting doesn't affect the space order on the original
   * floors.
   */
  static sortFloorsSpaces(floors: EDPFloorSpaceInfo[]): EDPFloorSpaceInfo[] {
    const bySuiteId = (spaceA: EDPSpaceInfo, spaceB: EDPSpaceInfo) => {
      if (spaceA.suite_id == null || spaceB.suite_id == null) {
        return spaceA.suite_id != null ? 1 : -1;
      } else {
        return spaceA.suite_id >= spaceB.suite_id ? 1 : -1;
      }
    };
    return floors.map(floor =>
      // sort will apply the order in the existing array, therefore, I'm creating a new reference
      floor.spaces != null ? { ...floor, spaces: [...floor.spaces].sort(bySuiteId) } : null
    );
  }

  spaces: AbstractSpaceClass[];

  constructor(
    public floorId: number,
    public floorIndex: number,
    public floorNumber: string,
    public floorDesc: string,
    public floorArea: number,
    public floorAreaUOM: string,
    public floorHeight: number,
    public floorHeightUOM: string,
    public totalGrossArea: number,
    public totalGrossAreaUOM: string,
    public floorImageURL: string,
    public hideFloor: boolean,
    public floorPlanId: number,
    edpSpacesInfo: EDPSpaceInfo[] | AbstractSpaceClass[] = [],
    public occupiedArea?: number,
    public containsInactiveSpaces?: boolean,
    public isHighlighted?: boolean,
    private options: Partial<FloorOptions> = {}
  ) {
    /**
     * Since floor number is not available from edp contracts for Stacking Plan,
     * floor number info should be included from the floor class.
     * floor_number should be added to edpSpacesInfo only when it is not an instanceof AbstractSpaceClass.
     */
    const spacesWithFloor =
      edpSpacesInfo?.length && edpSpacesInfo[0] instanceof AbstractSpaceClass
        ? edpSpacesInfo
        : (edpSpacesInfo || []).map(spaceInfo => {
            return { ...spaceInfo, floor_number: floorNumber };
          });
    this.mapSpaces(spacesWithFloor);
  }

  /** Review if total floor area is exceeded by the sum of its spaces areas */
  isFloorAreaExceeded(): boolean {
    return this.floorArea != null && this.floorArea < this.getFloorSpaceAreaSum();
  }

  /**
   * Review if total floor area is exceeded or is equal
   * to the sum of its spaces areas
   */
  isFloorSpaceEqualOrExceeded(): boolean {
    return this.floorArea != null && this.floorArea <= this.getFloorSpaceAreaSum();
  }

  /** Floor is valid if index is greater than {@link InvalidIndex} */
  isIndexValid(): boolean {
    return FloorClass.isFloorIndexValid(this.floorIndex);
  }

  /** Floor is above ground if index is greater or equals to 0 */
  isAboveGround(): boolean {
    return this.isIndexValid() && this.floorIndex != null && this.floorIndex >= 0;
  }

  /**
   * Floor is under ground if index is lesser than 0 but greater than
   * {@link InvalidIndex}
   */
  isUnderGround(): boolean {
    return this.isIndexValid() && this.floorIndex != null && this.floorIndex < 0;
  }

  /**
   * Dynamically generates the floor plan file name from the url string.
   */
  generateFloorPlanFileName(): string {
    const splitUrl = this.floorImageURL != null ? this.floorImageURL.split('/') : null;
    return splitUrl != null ? splitUrl[splitUrl.length - 1] : null;
  }

  /** Retrieve the floor description or floor number */
  generateFloorLabel(showIndex = false): string | null {
    return `${showIndex ? `(${this.floorIndex}) ` : ''}${
      isNotNullOrEmptyString(this.floorDesc) ? this.floorDesc : this.floorNumber || this.floorIndex.toString()
    }`;
  }

  /**
   * Review if area & UOM exists and return the concatenated value. Return only
   * the area if UOM is not present, else returns null.
   */
  generateFloorSpace(mappedUomLov?: { [uomKey: string]: string }): string | null {
    let result = null;
    if (isNotNullOrEmptyString(this.floorArea) && isNotNullOrEmptyString(this.floorAreaUOM)) {
      let areaUom = this.floorAreaUOM;
      if (mappedUomLov != null) {
        const foundUom = mappedUomLov[this.floorAreaUOM];
        areaUom = foundUom != null ? areaUomAbbrev(foundUom) : areaUom;
      }
      result = `${formatNumber(this.floorArea)} ${areaUom}`;
    } else if (isNotNullOrEmptyString(this.floorArea) && !isNotNullOrEmptyString(this.floorAreaUOM)) {
      result = `${formatNumber(this.floorArea)}`;
    }
    return result;
  }

  generateFloorCatalogOption(options: Partial<FloorCatalogOptions> = {}): FloorOption {
    const { disableIfFloorNumberValue = true, useIndexAsValue = false, showIndex = false } = options;
    return {
      label: this.generateFloorLabel(showIndex),
      value: useIndexAsValue || !isNotNullOrEmptyString(this.floorNumber) ? `${this.floorIndex}` : this.floorNumber,
      disabled: disableIfFloorNumberValue ? !isNotNullOrEmptyString(this.floorNumber) : false,
      altValue: this.floorIndex,
    };
  }

  toEDPFloorSpaceInfo(): EDPFloorSpaceInfo {
    return {
      floor_id: this.floorId,
      floor_index: this.floorIndex,
      floor_number: this.floorNumber,
      floor_desc: this.floorDesc,
      floor_size: !this.floorArea ? null : this.floorArea,
      floor_size_uom_desc: this.floorAreaUOM,
      floor_slab_height: this.floorHeight,
      floor_slab_height_uom: this.floorHeightUOM,
      total_gross_area: this.totalGrossArea,
      total_gross_area_uom_desc: this.totalGrossAreaUOM,
      floor_plan_url: this.floorImageURL,
      hide_floor_f: this.hideFloor,
      floor_plan_id: this.floorPlanId,
      spaces: this.spaces.map(space => space.toEDPSpaceInfo()),
    };
  }

  clone() {
    return new FloorClass(
      this.floorId,
      this.floorIndex,
      this.floorNumber,
      this.floorDesc,
      this.floorArea,
      this.floorAreaUOM,
      this.floorHeight,
      this.floorHeightUOM,
      this.totalGrossArea,
      this.totalGrossAreaUOM,
      this.floorImageURL,
      this.hideFloor,
      this.floorPlanId,
      this.spaces, // NOTE: So far, cloning spaces is still not required
      this.occupiedArea,
      this.containsInactiveSpaces,
      this.isHighlighted,
      { ...this.options }
    );
  }

  getFloorSpaceAreaSum(): number {
    if (this.occupiedArea == null) {
      this.occupiedArea = this.spaces.reduce((acc, space) => acc + space.getAreaSpace() || 0, 0);
    }
    return this.occupiedArea;
  }

  isEmptyFloor() {
    return this.spaces.length <= 0;
  }

  public getUnaccountedSpace(): number {
    const unaccountedSpace = this.floorArea - this.getFloorSpaceAreaSum();
    return unaccountedSpace > 0 && !this.hideFloor ? Math.floor(unaccountedSpace) : null;
  }

  private mapSpaces(edpSpacesInfo: EDPSpaceInfo[] | AbstractSpaceClass[]): void {
    if (edpSpacesInfo != null && edpSpacesInfo[0] instanceof AbstractSpaceClass) {
      this.spaces = edpSpacesInfo as AbstractSpaceClass[];
    } else {
      const spacesAndInactiveFlags: SpacesAndInactiveFlag[] = ((edpSpacesInfo as EDPSpaceInfo[]) || []).map(space =>
        SpaceClass.fromEDPSpaceInfo(space, this.options)
      );
      this.containsInactiveSpaces =
        spacesAndInactiveFlags.some(({ anyInactive }) => anyInactive) || this.containsInactiveSpaces;
      this.spaces = spacesAndInactiveFlags.reduce((acc, { spaces }) => [...acc, ...spaces], []);
    }
  }

  toString(showIndex?: boolean): string {
    return this.generateFloorLabel(showIndex);
  }
}
